/**
 * Pure properties-record → lead brief model.
 * Never copies phones, emails, or invents numbers. Heir and skip names only.
 */
import { renderFamilyTreeText } from "../../../packages/core/src/family-tree-text.js";
import { FamilyGraphSchema } from "../../../packages/core/src/obituary-intestacy.js";
import { CLOSE_SKIP_CALLER_STATUS_LABELS } from "../../../packages/core/src/close-skip-fields.js";
import { sourceFieldsIncludeCourtDocket } from "../../../packages/core/src/nc-court-case.js";
import {
  auctionEquityProvenanceFromSourceFields,
  isAuctionEquityFormulaVersion,
  type AuctionEquityProvenance
} from "../../../packages/core/src/property-equity.js";

export type LeadBriefLookupKind = "ok" | "not_found" | "invalid_sp";

export type LeadBriefPdfDocument = {
  id: string;
  name: string | null;
  href: string | null;
  state?: "captured" | "missing" | "partial";
};

export type LeadBriefFact = {
  value: string | null;
  outcome: "accepted" | "missing" | "uncertain" | "unavailable";
  unit: string | null;
};

export type LeadBriefServiceParty = {
  name: string | null;
  role: string;
  heirIdentification: string;
  addresses: string[];
  inferred: boolean;
  pdfs: LeadBriefPdfDocument[];
};

export type LeadBriefDefaultAffidavit = {
  pdfs: LeadBriefPdfDocument[];
  statedPrincipal: LeadBriefFact;
  principalAsOfDate: LeadBriefFact;
  perDiemInterest: LeadBriefFact;
  annualInterestRate: LeadBriefFact;
};

export type LeadBriefServiceAffidavit = {
  pdfs: LeadBriefPdfDocument[];
  heirs: LeadBriefServiceParty[];
  others: LeadBriefServiceParty[];
};

export type LeadBriefEventDetail = {
  label: string;
  value: string;
};

export type LeadBriefCaseEvent = {
  date: string | null;
  type: string | null;
  description: string | null;
  documentName: string | null;
  pdf: LeadBriefPdfDocument | null;
  pdfs: LeadBriefPdfDocument[];
  details: LeadBriefEventDetail[];
};

export type LeadBriefAuctionEquity = {
  value: number | null;
  reason: string | null;
  status: string | null;
  freshness: string | null;
  currentAuctionAmount: number | null;
  currentAuctionAmountAsOf: string | null;
  currentAuctionAmountKind: string | null;
  affidavitPrincipal: number | null;
  affidavitPrincipalAsOf: string | null;
  lastKnownValue: number | null;
  lastKnownAt: string | null;
  formulaVersion: string | null;
};

export type LeadBriefHeir = {
  name: string;
  relationship: string;
  survival: string;
  inSkip: boolean | null;
};

export type LeadBriefSkipPerson = {
  name: string;
  role: string;
};

export type LeadBriefModel = {
  spNumber: string;
  treeLabel: string | null;
  county: string | null;
  saleDate: string | null;
  saleTime: string | null;
  daysToSale: number | null;
  ownerName: string | null;
  propertyAddress: string | null;
  mailingAddress: string | null;
  deceased: boolean | null;
  moreOwners: boolean | null;
  obituaryStatus: string | null;
  obituaryUrl: string | null;
  obituaryExcerpt: string | null;
  obituaryDecedentName: string | null;
  familyTreeText: string | null;
  heirsToCall: LeadBriefHeir[];
  skipPeople: LeadBriefSkipPerson[];
  avm: number | null;
  avmSource: string | null;
  zestimate: number | null;
  zillowHref: string | null;
  lastSaleDate: string | null;
  lastSaleAmount: number | null;
  plv: number | null;
  plvSource: string | null;
  estimatedEquity: number | null;
  openingBid: string | null;
  caseNumber: string | null;
  parcel: string | null;
  trustee: string | null;
  treeActive: string | null;
  skipTraceDate: string | null;
  skipTraceStatus: string | null;
  attomOwner: string | null;
  ecourtsSaleState: string | null;
  ecourtsLastUpsetDate: string | null;
  ecourtsLastUpsetAmount: string | null;
  ecourtsCurrentAmount: string | null;
  ecourtsUpsetFilingDate: string | null;
  ecourtsUpsetDeadline: string | null;
  ecourtsCheckedAt: string | null;
  ecourtsFreshness: string | null;
  ecourtsCaseEvents: LeadBriefCaseEvent[];
  ecourtsPdfDocuments: LeadBriefPdfDocument[];
  galDocuments: LeadBriefPdfDocument[];
  ecourtsAuctionStatus: Record<string, unknown> | null;
  defaultAffidavit: LeadBriefDefaultAffidavit;
  serviceAffidavit: LeadBriefServiceAffidavit;
  auctionEquity: LeadBriefAuctionEquity;
  spLookupArtifact: string | null;
  closeLeadId: string | null;
  googleObitHref: string | null;
  stageLabel: string;
  lookupOutcome?: LeadBriefLookupKind;
};

const MAX_CASE_EVENTS = 5;
const ECOURTS_PDF_URI_PREFIX = "r2://county-preforeclosure-artifacts/toolbench/ecourts-sp-lookup/";
const BRIEF_PDF_ID = /^[A-Za-z0-9._-]+$/;

export type LeadBriefMapResult = {
  kind: LeadBriefLookupKind;
  requestedSp: string;
  brief: LeadBriefModel;
};

const TREE_SOURCE_ORDER = [
  "logs_upcoming",
  "logs_held",
  "brock",
  "hutchens",
  "ingle",
  "bell",
  "mtg",
  "catawba",
  "logs"
] as const;

const TREE_SOURCE_LABELS: Record<string, string> = {
  brock: "Brock and Scott",
  hutchens: "Hutchens",
  ingle: "Ingle",
  catawba: "Catawba",
  bell: "Bell Carrington",
  logs: "LOGS",
  logs_upcoming: "Logs upcoming",
  logs_held: "Logs held",
  mtg: "MTG"
};

const STAGE_LABELS: Record<string, string> = {
  ready_to_call: "Ready to call",
  on_hold: "On hold",
  gated_block: "Blocked",
  gated_pass: "Gated pass",
  close_pushed: "In Close",
  ...CLOSE_SKIP_CALLER_STATUS_LABELS,
  valued: "Valued",
  skip_complete: "Skip complete",
  skip_ready: "Skip ready",
  needs_owner: "Needs owner",
  ingested: "Ingested"
};

export function normalizeBriefSp(value: string): string | null {
  const normalized = String(value ?? "")
    .replaceAll(/\s+/g, "")
    .toUpperCase()
    .replace(/^(\d{2})-(SP|CVS?)/, "$1$2");
  return /^(?:\d{2}SP\d{1,8}(?:-\d{3})?|\d{2}CVS?\d{4,8}(?:-\d{3})?)$/.test(normalized)
    ? normalized
    : null;
}

export function mapPropertyRecordToLeadBrief(
  record: Record<string, unknown> | null | undefined,
  requestedSp: string
): LeadBriefMapResult {
  const requested = String(requestedSp ?? "").trim();
  const normalized = normalizeBriefSp(requested);
  if (!normalized) {
    return {
      kind: "invalid_sp",
      requestedSp: requested || "—",
      brief: emptyBrief(requested || "—", "invalid_sp")
    };
  }
  if (!record) {
    return {
      kind: "not_found",
      requestedSp: normalized,
      brief: emptyBrief(normalized, "not_found")
    };
  }
  const fields = flattenSourceFields(parseJson(record.source_fields));
  if (!sourceFieldsIncludeCourtDocket(record.source_fields, normalized)) {
    return {
      kind: "not_found",
      requestedSp: normalized,
      brief: emptyBrief(normalized, "not_found")
    };
  }
  const skip = parseJson(record.skip_payload);
  const ecourts = asRecord(fields.ecourts);
  const projection = asRecord(record.ecourts_case);
  const ecourtsAuctionStatus = asRecord(ecourts.auctionStatusDetails ?? ecourts.auction_status);
  const nestedUpset = asRecord(ecourtsAuctionStatus.lastUpsetBid ?? ecourts.lastUpsetBid);
  const treeKey = treeSourceKey(record.sources);
  const mailing = mailingAddress(record);
  const propertyAddress = propertyLine(record);
  const capturedDocs = [
    ...records(projection.documents),
    ...capturedDocumentRecords(ecourts)
  ];
  const ecourtsPdfDocuments = mapEcourtsPdfDocuments(capturedDocs, normalized);
  const galDocuments = ecourtsPdfDocuments.filter((pdf) => isGalName(pdf.name));
  const skipPeople = skipPeopleFromPayload(skip);
  const familyGraph = familyGraphFromRecord(record, fields);
  const heirsToCall = heirsFromGraph(familyGraph, skipPeople);
  const lastSale = lastSaleFromAttom(record.attom_owner_json);
  const ownerName = text(record.owner_raw) || personName(record) || null;
  const conveyor = text(record.conveyor_state);
  const canonicalEvents = records(projection.latestFiveEvents);
  const ecourtsCaseEvents = canonicalEvents.length
    ? mapCanonicalEvents(canonicalEvents, capturedDocs, ecourtsPdfDocuments, normalized)
    : mapRecentCaseEvents(records(ecourts.caseEvents ?? ecourts.case_events), capturedDocs, ecourtsPdfDocuments);
  const status = asRecord(projection.status);
  return {
    kind: "ok",
    requestedSp: normalized,
    brief: {
      spNumber: normalized,
      treeLabel: treeKey ? TREE_SOURCE_LABELS[treeKey] ?? treeKey : null,
      county: text(record.county),
      saleDate: dateOnly(
        text(projection.scheduledSaleDate)
        || text(projection.heldSaleDate)
        || firstText(fields, ["auctionDate", "saleDate", "sale_date", "auction_date"])
      ),
      saleTime: firstText(fields, ["saleTime", "sale_time"]),
      daysToSale: null,
      ownerName,
      propertyAddress,
      mailingAddress: mailing && mailing !== propertyAddress ? mailing : mailing ? mailing : null,
      deceased: deceasedFlag(record.deceased),
      moreOwners: boolish(skip.moreOwners ?? skip.more_owners),
      obituaryStatus: text(record.obituary_match_status),
      obituaryUrl: text(record.obituary_url),
      obituaryExcerpt: excerpt(text(record.obituary_text)),
      obituaryDecedentName: text(familyGraph?.decedentName) || null,
      familyTreeText: familyTreeTextFromRecord(record, fields)
        || text(skip.familyTreeText)
        || text(record.family_tree_text)
        || text(record.familyTreeText),
      heirsToCall,
      skipPeople: extraSkipPeople(skipPeople, heirsToCall),
      avm: numberOrNull(record.attom_avm),
      avmSource: text(record.property_value_source) || (numberOrNull(record.attom_avm) != null ? "ATTOM" : null),
      zestimate: numberOrNull(record.zestimate),
      zillowHref: zillowSearchHref(propertyAddress),
      lastSaleDate: lastSale.date,
      lastSaleAmount: lastSale.amount,
      plv: numberOrNull(record.plv),
      plvSource: text(record.plv_source),
      estimatedEquity: numberOrNull(record.estimated_equity),
      openingBid: firstText(fields, ["openingBid", "opening_bid", "bid"]),
      caseNumber: firstText(fields, ["caseNumber", "case_number", "spNumber"]) || normalized,
      parcel: text(record.parcel),
      trustee: firstText(fields, ["trustee"]),
      treeActive: firstText(fields, ["active", "Active"]),
      skipTraceDate: text(skip.skipTraceDate) || text(skip.skip_trace_date),
      skipTraceStatus: text(skip.skipTraceStatus) || text(skip.skip_trace_status),
      attomOwner: attomOwnerName(record.attom_owner_json),
      ecourtsSaleState: text(status.saleState)
        || text(ecourts.saleState)
        || text(ecourts.sale_state)
        || firstText(fields, ["saleState"]),
      ecourtsLastUpsetDate: dateOnly(
        text(projection.upsetFilingDate)
        || text(ecourts.lastUpsetBidDate)
        || text(nestedUpset.date)
        || firstText(fields, ["lastUpset", "last_upset"])
      ),
      ecourtsLastUpsetAmount: amountText(
        projection.latestUpsetAmount
        ?? ecourts.lastUpsetBidAmount
        ?? nestedUpset.amount
      ),
      ecourtsCurrentAmount: amountText(
        hasCanonicalProjection(record)
          ? projection.currentAmount
          : nestedUpset.amount
      ),
      ecourtsUpsetFilingDate: dateOnly(text(projection.upsetFilingDate) || text(nestedUpset.date)),
      ecourtsUpsetDeadline: dateOnly(text(projection.upsetDeadline)),
      ecourtsCheckedAt: text(asRecord(projection.freshness).sourceCheckedAt)
        || text(ecourts.checkedAt)
        || text(ecourts.checked_at),
      ecourtsFreshness: ecourtsFreshnessLabel(projection, ecourts),
      ecourtsCaseEvents,
      ecourtsPdfDocuments,
      galDocuments,
      ecourtsAuctionStatus: Object.keys(status).length
        ? { ...ecourtsAuctionStatus, ...status }
        : Object.keys(ecourtsAuctionStatus).length ? ecourtsAuctionStatus : null,
      defaultAffidavit: mapDefaultAffidavit(projection, capturedDocs, ecourtsPdfDocuments, normalized),
      serviceAffidavit: mapServiceAffidavit(projection, capturedDocs, ecourtsPdfDocuments, normalized),
      auctionEquity: mapAuctionEquity(record, fields, projection),
      spLookupArtifact: firstText(fields, ["notice", "spLookupArtifact"]),
      closeLeadId: text(record.close_lead_id),
      googleObitHref: googleObitHref(ownerName, record),
      stageLabel: STAGE_LABELS[conveyor ?? ""] ?? conveyor ?? "—",
      lookupOutcome: "ok"
    }
  };
}

export function emptyBrief(requestedSp: string, kind: Exclude<LeadBriefLookupKind, "ok">): LeadBriefModel {
  return {
    spNumber: requestedSp,
    treeLabel: null,
    county: null,
    saleDate: null,
    saleTime: null,
    daysToSale: null,
    ownerName: null,
    propertyAddress: null,
    mailingAddress: null,
    deceased: null,
    moreOwners: null,
    obituaryStatus: null,
    obituaryUrl: null,
    obituaryExcerpt: null,
    obituaryDecedentName: null,
    familyTreeText: null,
    heirsToCall: [],
    skipPeople: [],
    avm: null,
    avmSource: null,
    zestimate: null,
    zillowHref: null,
    lastSaleDate: null,
    lastSaleAmount: null,
    plv: null,
    plvSource: null,
    estimatedEquity: null,
    openingBid: null,
    caseNumber: null,
    parcel: null,
    trustee: null,
    treeActive: null,
    skipTraceDate: null,
    skipTraceStatus: null,
    attomOwner: null,
    ecourtsSaleState: null,
    ecourtsLastUpsetDate: null,
    ecourtsLastUpsetAmount: null,
    ecourtsCurrentAmount: null,
    ecourtsUpsetFilingDate: null,
    ecourtsUpsetDeadline: null,
    ecourtsCheckedAt: null,
    ecourtsFreshness: null,
    ecourtsCaseEvents: [],
    ecourtsPdfDocuments: [],
    galDocuments: [],
    ecourtsAuctionStatus: null,
    defaultAffidavit: emptyDefaultAffidavit(),
    serviceAffidavit: emptyServiceAffidavit(),
    auctionEquity: emptyAuctionEquity(),
    spLookupArtifact: null,
    closeLeadId: null,
    googleObitHref: null,
    stageLabel: kind === "invalid_sp" ? "Invalid SP" : "Not found",
    lookupOutcome: kind
  };
}

function parseJson(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed as Record<string, unknown>;
  } catch {
    return {};
  }
  return {};
}

function flattenSourceFields(sourceFields: Record<string, unknown>): Record<string, unknown> {
  const flat: Record<string, unknown> = { ...sourceFields };
  for (const value of Object.values(sourceFields)) {
    if (value && typeof value === "object" && !Array.isArray(value)) Object.assign(flat, value);
  }
  return flat;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter((entry): entry is Record<string, unknown> => Boolean(entry && typeof entry === "object" && !Array.isArray(entry))) : [];
}

function text(value: unknown): string | null {
  if (value == null) return null;
  const raw = String(value).trim();
  return raw && raw !== "null" ? raw : null;
}

function firstText(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = text(record[key]);
    if (value) return value;
  }
  return null;
}

function numberOrNull(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) && n !== 0 ? n : Number.isFinite(n) ? n : null;
}

function amountText(value: unknown): string | null {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return text(value);
}

function dateOnly(value: string | null): string | null {
  if (!value) return null;
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  if (iso?.[1]) return iso[1];
  const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(value);
  if (!mdy) return value;
  let year = Number(mdy[3]);
  if (year < 100) year += 2000;
  return `${year.toString().padStart(4, "0")}-${mdy[1]!.padStart(2, "0")}-${mdy[2]!.padStart(2, "0")}`;
}

function deceasedFlag(value: unknown): boolean | null {
  if (value == null || value === "") return null;
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;
  const raw = String(value).trim().toLowerCase();
  if (raw === "yes" || raw === "true" || raw === "deceased") return true;
  if (raw === "no" || raw === "false" || raw === "living") return false;
  return null;
}

function boolish(value: unknown): boolean | null {
  if (value == null || value === "") return null;
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  const raw = String(value).trim().toLowerCase();
  if (raw === "yes" || raw === "true") return true;
  if (raw === "no" || raw === "false") return false;
  return null;
}

function personName(record: Record<string, unknown>): string | null {
  const first = text(record.first_name);
  const last = text(record.last_name);
  const middle = text(record.middle_name);
  const joined = [first, middle, last].filter(Boolean).join(" ");
  return joined || null;
}

function propertyLine(record: Record<string, unknown>): string | null {
  const street = text(record.street);
  const city = text(record.city);
  const state = text(record.state);
  const zip = text(record.zip);
  const tail = [city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  if (!street && !tail) return null;
  return tail ? `${street ?? ""}${street ? ", " : ""}${tail}` : street;
}

function mailingAddress(record: Record<string, unknown>): string | null {
  const street = text(record.mailing_street);
  if (!street) return null;
  const tail = [
    text(record.mailing_city),
    [text(record.mailing_state), text(record.mailing_zip)].filter(Boolean).join(" ")
  ].filter(Boolean).join(", ");
  return tail ? `${street}, ${tail}` : street;
}

function treeSourceKey(sources: unknown): string | null {
  const rec = parseJson(sources);
  for (const key of TREE_SOURCE_ORDER) {
    if (rec[key] != null) return key;
  }
  for (const key of Object.keys(rec)) {
    if (key && key !== "reportall" && key !== "manual" && key !== "pdf") return key;
  }
  return null;
}

function attomOwnerName(value: unknown): string | null {
  const rec = parseJson(value);
  return firstText(rec, ["ownerName", "owner_name", "name"]) || text(rec.owner);
}

function familyTreeTextFromRecord(record: Record<string, unknown>, fields: Record<string, unknown>): string | null {
  const stored = text(record.intestacy_tree) || text(fields.intestacyTree) || text(fields.familyTreeText);
  if (stored) return stored;
  const analysis = asRecord(fields.analysis);
  const graph = fields.familyGraph ?? analysis.familyGraph ?? asRecord(record.analysis).familyGraph;
  if (graph == null) return null;
  try {
    const rendered = renderFamilyTreeText(graph).trim();
    return rendered || null;
  } catch {
    return null;
  }
}

function capturedDocumentRecords(ecourts: Record<string, unknown>): Record<string, unknown>[] {
  return [
    ...records(ecourts.pdfDocuments),
    ...records(ecourts.pdf_documents),
    ...records(ecourts.caseEventDocuments),
    ...records(ecourts.documents)
  ];
}

function mapEcourtsPdfDocuments(captured: Record<string, unknown>[], sp: string): LeadBriefPdfDocument[] {
  const seen = new Map<string, LeadBriefPdfDocument>();
  for (const entry of captured) {
    const artifactUri = capturedArtifactUri(entry);
    const id = briefPdfId(entry);
    if (!id || !artifactUri || !isLocalEcourtsArtifact(artifactUri) || seen.has(id)) continue;
    seen.set(id, {
      id,
      name: documentNameOf(entry),
      href: `/dashboard/brief/${encodeURIComponent(sp)}/pdf/${encodeURIComponent(id)}`,
      state: "captured"
    });
  }
  return [...seen.values()];
}

function mapRecentCaseEvents(
  events: Record<string, unknown>[],
  captured: Record<string, unknown>[],
  pdfs: LeadBriefPdfDocument[]
): LeadBriefCaseEvent[] {
  return events
    .map((event, index) => ({ event, index, time: eventTime(event) }))
    .sort((left, right) => right.time - left.time || left.index - right.index)
    .slice(0, MAX_CASE_EVENTS)
    .map(({ event }) => mapCaseEvent(event, captured, pdfs));
}

function mapCaseEvent(
  raw: Record<string, unknown>,
  captured: Record<string, unknown>[],
  pdfs: LeadBriefPdfDocument[]
): LeadBriefCaseEvent {
  const body = asRecord(raw.Event);
  const typeId = asRecord(body.TypeId ?? body.TypeID);
  const docs = [...records(body.Documents), ...records(raw.Documents)];
  const descriptionParts = [text(typeId.Description), text(body.Comment)].filter((value, index, all) => {
    return Boolean(value) && all.indexOf(value) === index;
  });
  const matched = matchEventPdfs(docs, captured, pdfs);
  const documentName = docs.map((doc) => text(doc.DocumentName) || documentNameOf(doc)).find(Boolean) ?? null;
  return {
    date: dateOnly(
      text(body.Date)
      || text(body.EventDate)
      || text(raw.SortEventDate)
      || isoTimestampDate(text(raw.TimestampCreateIso8601))
    ),
    type: text(typeId.Word) || text(typeId.Code),
    description: descriptionParts.join(" · ") || null,
    documentName,
    pdf: matched.find((pdf) => pdf.href) ?? matched[0] ?? null,
    pdfs: matched.length ? matched : missingEventPdfs(docs),
    details: eventDetailsFromRaw(raw, body, typeId)
  };
}

function matchEventPdfs(
  eventDocs: Record<string, unknown>[],
  captured: Record<string, unknown>[],
  pdfs: LeadBriefPdfDocument[]
): LeadBriefPdfDocument[] {
  if (!eventDocs.length) return [];
  const byId = new Map(pdfs.map((pdf) => [pdf.id, pdf]));
  const indexed = captured.flatMap((entry) => {
    const pdf = byId.get(briefPdfId(entry) ?? "");
    if (!pdf) return [];
    const inner = asRecord(entry.document);
    const keys = [
      briefPdfId(entry),
      text(entry.source_document_id),
      text(entry.sourceDocumentId),
      text(inner.DocumentID),
      text(entry.DocumentID),
      documentNameOf(entry)?.toLowerCase()
    ].filter((value): value is string => Boolean(value));
    return keys.map((key) => ({ key, pdf }));
  });
  const found: LeadBriefPdfDocument[] = [];
  const seen = new Set<string>();
  for (const doc of eventDocs) {
    const inner = asRecord(doc.document);
    const candidates = [
      text(doc.source_document_id),
      text(doc.sourceDocumentId),
      text(doc.DocumentID),
      text(inner.DocumentID),
      (text(doc.DocumentName) || documentNameOf(doc))?.toLowerCase()
    ].filter((value): value is string => Boolean(value));
    let hit: LeadBriefPdfDocument | null = null;
    for (const key of candidates) {
      hit = indexed.find((entry) => entry.key === key)?.pdf ?? byId.get(key) ?? null;
      if (hit) break;
    }
    if (hit) {
      if (seen.has(hit.id)) continue;
      seen.add(hit.id);
      found.push(hit);
      continue;
    }
    const missing = missingPdfFromDoc(doc);
    if (missing && !seen.has(missing.id)) {
      seen.add(missing.id);
      found.push(missing);
    }
  }
  return found;
}

function missingEventPdfs(docs: Record<string, unknown>[]): LeadBriefPdfDocument[] {
  const out: LeadBriefPdfDocument[] = [];
  const seen = new Set<string>();
  for (const doc of docs) {
    const missing = missingPdfFromDoc(doc);
    if (!missing || seen.has(missing.id)) continue;
    seen.add(missing.id);
    out.push(missing);
  }
  return out;
}

function missingPdfFromDoc(doc: Record<string, unknown>): LeadBriefPdfDocument | null {
  const inner = asRecord(doc.document);
  const name = text(doc.DocumentName) || documentNameOf(doc);
  const id = text(doc.source_document_id)
    || text(doc.sourceDocumentId)
    || text(doc.DocumentID)
    || text(inner.DocumentID)
    || name;
  if (!id) return null;
  return { id, name, href: null, state: "missing" };
}

function capturedArtifactUri(entry: Record<string, unknown>): string | null {
  return text(entry.durablePdfUri) || text(entry.durable_pdf_uri) || text(entry.artifactUri) || text(entry.artifact_uri);
}

function briefPdfId(entry: Record<string, unknown>): string | null {
  const id = text(entry.source_document_id)
    || text(entry.sourceDocumentId)
    || text(entry.id)
    || text(entry.pdf_asset_id)
    || text(entry.pdfAssetId);
  if (id && BRIEF_PDF_ID.test(id)) return id;
  const file = capturedArtifactUri(entry)?.split("/").pop()?.replace(/\.pdf$/i, "") ?? "";
  return file && BRIEF_PDF_ID.test(file) ? file : null;
}

function isLocalEcourtsArtifact(uri: string): boolean {
  if (!uri.startsWith(ECOURTS_PDF_URI_PREFIX) || uri.includes("..")) return false;
  return /\.pdf$/i.test(uri.split("?")[0] ?? "");
}

function documentNameOf(entry: Record<string, unknown>): string | null {
  const inner = asRecord(entry.document);
  return text(entry.documentName)
    || text(entry.DocumentName)
    || text(inner.DocumentName)
    || text(inner.documentName)
    || uriFileName(capturedArtifactUri(entry));
}

function uriFileName(uri: string | null): string | null {
  if (!uri) return null;
  const file = uri.split("?")[0]?.split("/").pop()?.trim() ?? "";
  if (!file) return null;
  return file.replace(/(\.pdf)+$/i, ".pdf");
}

function affidavitPdfMatches(pdf: LeadBriefPdfDocument, namePattern: RegExp): boolean {
  const haystack = [pdf.name, pdf.id, pdf.href].filter(Boolean).join(" ").replace(/[-_]+/g, " ");
  return namePattern.test(haystack);
}

function eventTime(entry: Record<string, unknown>): number {
  const event = asRecord(entry.Event);
  const value = event.Date ?? event.EventDate ?? entry.SortEventDate ?? entry.TimestampCreateIso8601 ?? entry.TimestampCreate;
  const time = Date.parse(String(value ?? ""));
  return Number.isFinite(time) ? time : 0;
}

function isoTimestampDate(value: string | null): string | null {
  if (!value) return null;
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return iso?.[1] ?? value;
}

function familyGraphFromRecord(record: Record<string, unknown>, fields: Record<string, unknown>): Record<string, unknown> | null {
  const graph = fields.familyGraph ?? asRecord(fields.analysis).familyGraph ?? asRecord(record.analysis).familyGraph;
  const parsed = FamilyGraphSchema.safeParse(graph);
  return parsed.success ? parsed.data as unknown as Record<string, unknown> : graph && typeof graph === "object" && !Array.isArray(graph) ? graph as Record<string, unknown> : null;
}

function skipPeopleFromPayload(skip: Record<string, unknown>): LeadBriefSkipPerson[] {
  const people = Array.isArray(skip.people) ? skip.people : [];
  const out: LeadBriefSkipPerson[] = [];
  for (const raw of people) {
    const rec = asRecord(raw);
    const name = text(rec.name);
    if (!name) continue;
    out.push({ name, role: text(rec.role) || "relative" });
  }
  return out;
}

function heirsFromGraph(graph: Record<string, unknown> | null, skipPeople: LeadBriefSkipPerson[]): LeadBriefHeir[] {
  const persons = records(graph?.persons);
  return persons
    .filter((person) => text(person.survival) === "apparently_survived" && person.individuallyNamed !== false)
    .map((person) => {
      const name = text(person.name) || "Unnamed";
      return {
        name,
        relationship: (text(person.relationship) || "other").replaceAll("_", " "),
        survival: "survived",
        inSkip: skipPeople.length ? skipPeople.some((skip) => namesMatch(skip.name, name)) : null
      };
    });
}

function extraSkipPeople(skipPeople: LeadBriefSkipPerson[], heirs: LeadBriefHeir[]): LeadBriefSkipPerson[] {
  return skipPeople.filter((person) => person.role !== "subject" && !heirs.some((heir) => namesMatch(heir.name, person.name)));
}

function namesMatch(left: string, right: string): boolean {
  const a = nameTokens(left);
  const b = nameTokens(right);
  if (!a.last || !b.last || a.last !== b.last) return false;
  if (!a.first || !b.first) return true;
  return a.first === b.first || a.first.startsWith(b.first) || b.first.startsWith(a.first);
}

function nameTokens(value: string): { first: string; last: string } {
  const parts = value.normalize("NFKD").replace(/\p{M}/gu, "").toUpperCase().replace(/[^A-Z\s]/g, " ").trim().split(/\s+/).filter(Boolean);
  return { first: parts[0] ?? "", last: parts[parts.length - 1] ?? "" };
}

function isGalName(name: string | null | undefined): boolean {
  return /guardian\s*ad\s*litem|\bg\.?a\.?l\.?\b/i.test(name ?? "");
}

function excerpt(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.replace(/\s+/g, " ").trim();
  if (!trimmed) return null;
  return trimmed.length > 480 ? `${trimmed.slice(0, 477).trimEnd()}…` : trimmed;
}

function zillowSearchHref(address: string | null): string | null {
  if (!address) return null;
  return `https://www.zillow.com/homes/${encodeURIComponent(address)}_rb/`;
}

function googleObitHref(owner: string | null, record: Record<string, unknown>): string | null {
  const parts = [owner, "obituary", text(record.city), text(record.state)].filter(Boolean);
  if (!owner) return null;
  return `https://www.google.com/search?q=${encodeURIComponent(parts.join(" "))}`;
}

function lastSaleFromAttom(value: unknown): { date: string | null; amount: number | null } {
  const rec = parseJson(value);
  const sale = asRecord(rec.sale ?? rec.lastSale ?? rec.saleSearch ?? asRecord(rec.sourceMetadata).sale);
  const date = dateOnly(firstText(sale, ["saleDate", "saleSearchDate", "lastSaleDate", "date"]) ?? text(rec.lastSaleDate));
  const amount = numberOrNull(sale.saleAmt ?? sale.amount ?? sale.lastSaleAmount ?? rec.lastSaleAmount);
  return { date, amount };
}

function emptyFact(): LeadBriefFact {
  return { value: null, outcome: "unavailable", unit: null };
}

function emptyDefaultAffidavit(): LeadBriefDefaultAffidavit {
  return {
    pdfs: [],
    statedPrincipal: emptyFact(),
    principalAsOfDate: emptyFact(),
    perDiemInterest: emptyFact(),
    annualInterestRate: emptyFact()
  };
}

function emptyServiceAffidavit(): LeadBriefServiceAffidavit {
  return { pdfs: [], heirs: [], others: [] };
}

function emptyAuctionEquity(): LeadBriefAuctionEquity {
  return {
    value: null,
    reason: "unavailable",
    status: null,
    freshness: null,
    currentAuctionAmount: null,
    currentAuctionAmountAsOf: null,
    currentAuctionAmountKind: null,
    affidavitPrincipal: null,
    affidavitPrincipalAsOf: null,
    lastKnownValue: null,
    lastKnownAt: null,
    formulaVersion: null
  };
}

function mapCanonicalEvents(
  events: Record<string, unknown>[],
  captured: Record<string, unknown>[],
  pdfs: LeadBriefPdfDocument[],
  sp: string
): LeadBriefCaseEvent[] {
  return events.slice(0, MAX_CASE_EVENTS).map((event) => {
    const original = asRecord(event.original);
    const body = asRecord(original.Event);
    const meta = asRecord(event.metadata);
    const typeId = asRecord(body.TypeId ?? body.TypeID);
    const docs = [...records(body.Documents), ...records(original.Documents)];
    const attachmentIds = Array.isArray(event.attachmentSourceDocumentIds)
      ? event.attachmentSourceDocumentIds.map((id) => String(id))
      : [];
    const namedDocs = attachmentIds.map((id) => {
      const match = docs.find((doc) => text(doc.DocumentID) === id || text(asRecord(doc.document).DocumentID) === id);
      return match ?? { DocumentID: id, source_document_id: id };
    });
    const eventDocs = namedDocs.length ? namedDocs : docs;
    const matched = matchEventPdfs(eventDocs, captured, pdfs).map((pdf) => (
      pdf.href ? pdf : { ...pdf, href: pdf.href ?? briefPdfHref(sp, pdf.id, pdf.state) }
    ));
    const descriptionParts = [
      text(meta.typeDescription) || text(typeId.Description),
      text(meta.comment) || text(body.Comment)
    ].filter((value, index, all) => Boolean(value) && all.indexOf(value) === index);
    const documentName = docs.map((doc) => text(doc.DocumentName) || documentNameOf(doc)).find(Boolean) ?? null;
    return {
      date: dateOnly(text(event.sourceDate) || text(meta.sourceDate) || text(body.Date)),
      type: text(meta.typeWord) || text(typeId.Word) || text(typeId.Code),
      description: descriptionParts.join(" · ") || null,
      documentName,
      pdf: matched.find((pdf) => pdf.href) ?? matched[0] ?? null,
      pdfs: matched.length ? matched : missingEventPdfs(eventDocs),
      details: eventDetailsFromCanonical(event, body, meta, typeId)
    };
  });
}

function briefPdfHref(sp: string, id: string, state: LeadBriefPdfDocument["state"]): string | null {
  if (state === "missing" || state === "partial") return null;
  return BRIEF_PDF_ID.test(id) ? `/dashboard/brief/${encodeURIComponent(sp)}/pdf/${encodeURIComponent(id)}` : null;
}

function eventDetailsFromRaw(
  raw: Record<string, unknown>,
  body: Record<string, unknown>,
  typeId: Record<string, unknown>
): LeadBriefEventDetail[] {
  return detailsFromPairs([
    ["Event ID", text(raw.EventId) || text(body.EventId)],
    ["Comment", text(body.Comment) || text(raw.Comment)],
    ["Amount", amountText(body.EventAmount ?? raw.EventAmount)],
    ["Type code", text(typeId.CodeID) || text(typeId.Code)],
    ["Index", amountText(body.IndexNumber ?? raw.IndexNumber)]
  ]);
}

function eventDetailsFromCanonical(
  event: Record<string, unknown>,
  body: Record<string, unknown>,
  meta: Record<string, unknown>,
  typeId: Record<string, unknown>
): LeadBriefEventDetail[] {
  const preferred = detailsFromPairs([
    ["Event ID", text(event.sourceEventId) || text(event.stableEventId)],
    ["Comment", text(meta.comment) || text(body.Comment)],
    ["Amount", amountText(meta.amount ?? body.EventAmount)],
    ["Type code", text(meta.typeCodeId) || text(typeId.CodeID)],
    ["Observed", text(event.observedAt)],
    ["Revision", event.revisionNo != null ? String(event.revisionNo) : null]
  ]);
  return mergeEventDetails(preferred, collectEventDetails(meta, body, asRecord(event.original), event));
}

function collectEventDetails(...records: Record<string, unknown>[]): LeadBriefEventDetail[] {
  const out: LeadBriefEventDetail[] = [];
  const seen = new Set<string>();
  for (const rec of records) {
    for (const [key, value] of Object.entries(rec)) {
      if (SKIP_EVENT_DETAIL_KEY.test(key)) continue;
      if (value && typeof value === "object") continue;
      const rendered = amountText(value) || text(value);
      if (!rendered || SKIP_EVENT_DETAIL_VALUE.test(rendered)) continue;
      const labelKey = key.toLowerCase();
      if (seen.has(labelKey)) continue;
      seen.add(labelKey);
      out.push({ label: key, value: rendered });
    }
  }
  return out;
}

function mergeEventDetails(preferred: LeadBriefEventDetail[], extra: LeadBriefEventDetail[]): LeadBriefEventDetail[] {
  const seen = new Set(preferred.map((detail) => detail.label.toLowerCase()));
  const out = [...preferred];
  for (const detail of extra) {
    const key = detail.label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(detail);
  }
  return out;
}

const SKIP_EVENT_DETAIL_KEY = /^(documents?|event|original|metadata|typeId|typeid|documentversions?|documentfragments?|documentid|fragment|source_url|sourceurl|displaydoc|url|pdfs?)$/i;
const SKIP_EVENT_DETAIL_VALUE = /displaydoc|^https?:\/\//i;

function detailsFromPairs(pairs: Array<[string, string | null]>): LeadBriefEventDetail[] {
  return pairs
    .filter((entry): entry is [string, string] => Boolean(entry[1]))
    .map(([label, value]) => ({ label, value }));
}

function mapDefaultAffidavit(
  projection: Record<string, unknown>,
  captured: Record<string, unknown>[],
  pdfs: LeadBriefPdfDocument[],
  sp: string
): LeadBriefDefaultAffidavit {
  const affidavit = asRecord(projection.affidavitFacts);
  const facts = asRecord(affidavit.facts);
  const defaultFacts = asRecord(facts.default ?? facts.affidavitOfDefault ?? facts.defaultAffidavit);
  const merged = { ...facts, ...defaultFacts };
  const ids = documentIdsForFields(affidavit, merged, ["statedPrincipal", "principalAsOfDate", "perDiemInterest", "annualInterestRate"]);
  return {
    pdfs: affidavitPdfs(ids, captured, pdfs, sp, /affidavit of default/i),
    statedPrincipal: mapFact(merged.statedPrincipal, "money"),
    principalAsOfDate: mapFact(merged.principalAsOfDate ?? merged.asOfDate ?? merged.principalAsOf, "date"),
    perDiemInterest: mapFact(merged.perDiemInterest ?? merged.dailyInterest ?? merged.perDiem, "per_diem"),
    annualInterestRate: mapFact(merged.annualInterestRate ?? merged.annualRate ?? merged.interestRate, "rate")
  };
}

function mapServiceAffidavit(
  projection: Record<string, unknown>,
  captured: Record<string, unknown>[],
  pdfs: LeadBriefPdfDocument[],
  sp: string
): LeadBriefServiceAffidavit {
  const affidavit = asRecord(projection.affidavitFacts);
  const facts = asRecord(affidavit.facts);
  const service = asRecord(facts.service ?? facts.affidavitOfService ?? facts.serviceAffidavit);
  const parties = [...records(service.parties), ...records(facts.parties)];
  const inferred = [
    ...records(service.inferredPeople),
    ...records(facts.inferredPeople),
    ...records(service.inferredPeoplePreserved),
    ...records(facts.inferredPeoplePreserved)
  ];
  const ids = documentIdsForFields(affidavit, { ...facts, ...service }, ["parties", "heirs"]);
  const pdfRefs = affidavitPdfs(ids, captured, pdfs, sp, /affidavit of service/i);
  const heirs: LeadBriefServiceParty[] = [];
  const others: LeadBriefServiceParty[] = [];
  for (const party of parties) {
    const mapped = mapServiceParty(party, pdfRefs, false);
    if (mapped.heirIdentification === "explicit_heir") heirs.push(mapped);
    else others.push(mapped);
  }
  for (const person of inferred) {
    others.push(mapServiceParty(person, pdfRefs, true));
  }
  return { pdfs: pdfRefs, heirs, others };
}

function mapServiceParty(party: Record<string, unknown>, pdfs: LeadBriefPdfDocument[], inferred: boolean): LeadBriefServiceParty {
  const nameRec = asRecord(party.name);
  const name = text(nameRec.normalizedValue) || text(party.name) || text(party.normalizedValue) || text(party.normalizedName) || "Unnamed";
  const heirIdentification = text(party.heirIdentification) || (inferred ? "inferred" : "uncertain");
  const roles = records(party.statedRoles);
  const role = roles.map((entry) => text(entry.rawText) || roleKindLabel(text(entry.kind))).filter(Boolean).join(", ")
    || roleKindLabel(text(party.role))
    || roleKindLabel(heirIdentification);
  const addresses = records(party.addresses)
    .map((entry) => text(entry.normalizedValue) || text(entry.rawText))
    .filter((value): value is string => Boolean(value));
  const single = text(party.address);
  if (single && !addresses.includes(single)) addresses.push(single);
  return {
    name,
    role,
    heirIdentification,
    addresses,
    inferred,
    pdfs: pdfsForParty(party, pdfs)
  };
}

function pdfsForParty(party: Record<string, unknown>, pdfs: LeadBriefPdfDocument[]): LeadBriefPdfDocument[] {
  const ids = [
    text(party.sourceDocumentId),
    text(party.source_document_id),
    text(party.documentVersionId),
    ...records(party.documents).map((doc) => text(doc.sourceDocumentId) || text(doc.DocumentID) || text(doc.id))
  ].filter((value): value is string => Boolean(value));
  if (!ids.length) return pdfs;
  return pdfs.filter((pdf) => ids.includes(pdf.id));
}

function roleKindLabel(kind: string | null): string {
  if (!kind) return "role unknown";
  if (kind === "explicit_heir" || kind === "heir") return "heir";
  if (kind === "unknown_heirs") return "unknown heirs";
  if (kind === "not_heir") return "not an heir";
  if (kind === "inferred") return "inferred";
  if (kind === "uncertain") return "role unknown";
  return kind.replaceAll("_", " ");
}

function mapFact(value: unknown, kind: "money" | "date" | "per_diem" | "rate"): LeadBriefFact {
  if (value == null || value === "") return emptyFact();
  if (typeof value === "number" || typeof value === "string") {
    const formatted = formatFactValue(value, kind, null);
    if (formatted == null) return { value: null, outcome: "unavailable", unit: unitFor(kind, null) };
    return { value: formatted, outcome: "accepted", unit: unitFor(kind, null) };
  }
  const rec = asRecord(value);
  const outcomeRaw = text(rec.outcome);
  const outcome = outcomeRaw === "accepted" || outcomeRaw === "missing" || outcomeRaw === "uncertain"
    ? outcomeRaw
    : rec.normalizedValue == null && rec.value == null && rec.rawText == null ? "unavailable" : "accepted";
  const raw = rec.normalizedValue ?? rec.value ?? rec.rawText;
  const unit = text(rec.unit);
  if (outcome === "missing" || outcome === "uncertain" || outcome === "unavailable" || raw == null || raw === "") {
    return {
      value: null,
      outcome: outcome === "accepted" ? "unavailable" : outcome,
      unit: unitFor(kind, unit)
    };
  }
  return { value: formatFactValue(raw, kind, unit), outcome: "accepted", unit: unitFor(kind, unit) };
}

function formatFactValue(value: unknown, kind: "money" | "date" | "per_diem" | "rate", unit: string | null): string | null {
  if (kind === "date") return dateOnly(text(value)) ?? text(value);
  if (typeof value === "number" && Number.isFinite(value)) {
    if (kind === "rate") return `${trimNumber(value)}${unit && unit.includes("%") ? "" : "%"}`;
    return kind === "per_diem" ? `${formatMoneyExact(value)}${unit ? ` ${unit}` : " / day"}` : formatMoneyExact(value);
  }
  const raw = text(value);
  if (!raw) return null;
  if (kind === "money" || kind === "per_diem") {
    const n = Number(raw.replace(/[$,]/g, ""));
    if (Number.isFinite(n)) {
      const money = formatMoneyExact(n);
      return kind === "per_diem" ? `${money}${unit ? ` ${unit}` : /day/i.test(raw) ? "" : " / day"}` : money;
    }
  }
  if (kind === "rate" && unit && !raw.includes(unit)) return `${raw} ${unit}`;
  return raw;
}

function formatMoneyExact(value: number): string {
  return value.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function trimNumber(value: number): string {
  return String(Number(value.toPrecision(12)));
}

function unitFor(kind: "money" | "date" | "per_diem" | "rate", unit: string | null): string | null {
  if (unit) return unit;
  if (kind === "per_diem") return "USD / day";
  if (kind === "rate") return "percent per annum";
  if (kind === "money") return "USD";
  return null;
}

function documentIdsForFields(
  affidavit: Record<string, unknown>,
  facts: Record<string, unknown>,
  fields: string[]
): string[] {
  const links = Array.isArray(affidavit.links) ? affidavit.links : [];
  const fromLinks = links
    .map((link) => asRecord(link))
    .filter((link) => !text(link.field) || fields.includes(text(link.field)!))
    .map((link) => text(link.documentVersionId) || text(link.sourceDocumentId))
    .filter((value): value is string => Boolean(value));
  const versions = Array.isArray(affidavit.sourceDocumentVersionIds)
    ? affidavit.sourceDocumentVersionIds.map((id) => String(id))
    : [];
  const fromFacts = fields.flatMap((field) => {
    const rec = asRecord(facts[field]);
    return [text(rec.documentVersionId), text(rec.sourceDocumentId), text(asRecord(rec.evidence).documentVersionId)];
  }).filter((value): value is string => Boolean(value));
  const ids = [...new Set([...fromLinks, ...fromFacts])];
  return ids.length ? ids : versions;
}

function affidavitPdfs(
  ids: string[],
  captured: Record<string, unknown>[],
  pdfs: LeadBriefPdfDocument[],
  sp: string,
  namePattern: RegExp
): LeadBriefPdfDocument[] {
  const byId = new Map(pdfs.map((pdf) => [pdf.id, pdf]));
  const named = pdfs.filter((pdf) => affidavitPdfMatches(pdf, namePattern));
  const out: LeadBriefPdfDocument[] = [];
  const seen = new Set<string>();
  const push = (pdf: LeadBriefPdfDocument | null | undefined) => {
    if (!pdf || seen.has(pdf.id)) return;
    seen.add(pdf.id);
    out.push(pdf);
  };
  for (const id of ids) {
    push(byId.get(id));
    const capturedHit = captured.find((entry) => briefPdfId(entry) === id || text(entry.sourceDocumentId) === id || text(entry.id) === id);
    if (capturedHit) {
      const artifactUri = capturedArtifactUri(capturedHit);
      const pdfId = briefPdfId(capturedHit) ?? id;
      if (artifactUri && isLocalEcourtsArtifact(artifactUri)) {
        push({
          id: pdfId,
          name: documentNameOf(capturedHit) || pdfId,
          href: `/dashboard/brief/${encodeURIComponent(sp)}/pdf/${encodeURIComponent(pdfId)}`,
          state: "captured"
        });
      } else {
        push({
          id: pdfId,
          name: documentNameOf(capturedHit) || pdfId,
          href: null,
          state: text(capturedHit.acquisitionOutcome) === "partial" ? "partial" : "missing"
        });
      }
    } else if (!byId.has(id)) {
      push({ id, name: id, href: null, state: "missing" });
    }
  }
  for (const pdf of named) push(pdf);
  return out;
}

function mapAuctionEquity(
  record: Record<string, unknown>,
  fields: Record<string, unknown>,
  projection: Record<string, unknown>
): LeadBriefAuctionEquity {
  const empty = emptyAuctionEquity();
  const raw = asRecord(projection.auctionEquity);
  const provenance = auctionEquityProvenanceFromSourceFields(fields);
  const column = numberKeepingZero(record.auction_equity);
  const columnVersion = text(record.auction_equity_formula_version);
  const columnInvalidation = text(record.auction_equity_invalidation_state);
  const freshness = ecourtsFreshnessLabel(projection, asRecord(fields.ecourts));
  if (looksLikeProvenance(raw)) {
    return fromProvenanceLike(raw, projection, column, freshness);
  }
  if (provenance) {
    return fromStoredProvenance(provenance, column, columnVersion, columnInvalidation, projection, freshness);
  }
  const inputs = canonicalAmountInputs(projection, raw);
  if (column != null) {
    const v2 = isAuctionEquityFormulaVersion(columnVersion);
    const invalidated = columnInvalidation === "authoritative_invalidation";
    return {
      ...empty,
      ...inputs,
      value: v2 && !invalidated ? column : null,
      reason: v2
        ? (invalidated ? "authoritative_invalidation" : null)
        : (columnVersion ? "legacy_formula_value" : "unavailable"),
      status: columnInvalidation,
      freshness,
      lastKnownValue: numberKeepingZero(record.auction_equity_last_known) ?? column,
      lastKnownAt: text(record.auction_equity_last_known_at),
      formulaVersion: columnVersion
    };
  }
  if (raw.value != null || raw.auctionPrice != null || raw.plv != null) {
    return {
      ...empty,
      ...inputs,
      value: null,
      reason: "legacy_formula_value",
      freshness,
      lastKnownValue: numberKeepingZero(raw.value),
      formulaVersion: text(raw.formulaVersion) || (raw.plv != null ? "auction-equity-v1" : null)
    };
  }
  if (!Object.keys(projection).length) return empty;
  return {
    ...empty,
    ...inputs,
    reason: inputs.currentAuctionAmount == null
      ? "missing_current_amount"
      : inputs.affidavitPrincipal == null
        ? "missing_affidavit_principal"
        : freshness === "stale" ? "stale" : "unavailable",
    freshness
  };
}

function looksLikeProvenance(raw: Record<string, unknown>): boolean {
  return Boolean(
    text(raw.formulaVersion)
    || raw.currentAmount != null
    || raw.affidavitPrincipal != null
    || raw.unavailableReason
    || raw.invalidationState
    || raw.equity != null
  );
}

function fromProvenanceLike(
  raw: Record<string, unknown>,
  projection: Record<string, unknown>,
  column: number | null,
  freshness: string | null
): LeadBriefAuctionEquity {
  const formulaVersion = text(raw.formulaVersion);
  const v2 = isAuctionEquityFormulaVersion(formulaVersion) || (formulaVersion == null && raw.plv == null && (raw.currentAmount != null || raw.affidavitPrincipal != null));
  const invalidated = text(raw.invalidationState) === "authoritative_invalidation";
  const reason = text(raw.unavailableReason)
    || (!v2 ? "legacy_formula_value" : invalidated ? "authoritative_invalidation" : null);
  const currentValue = v2 && !invalidated && !reason
    ? numberKeepingZero(raw.equity ?? raw.value ?? column)
    : null;
  return {
    ...canonicalAmountInputs(projection, raw),
    value: currentValue,
    reason,
    status: text(raw.invalidationState),
    freshness,
    lastKnownValue: numberKeepingZero(raw.lastKnownEquity ?? raw.value ?? column),
    lastKnownAt: text(raw.lastKnownAt ?? raw.computedAt),
    formulaVersion
  };
}

function fromStoredProvenance(
  provenance: AuctionEquityProvenance,
  column: number | null,
  columnVersion: string | null,
  columnInvalidation: string | null,
  projection: Record<string, unknown>,
  freshness: string | null
): LeadBriefAuctionEquity {
  const formulaVersion = provenance.formulaVersion || columnVersion;
  const v2 = isAuctionEquityFormulaVersion(formulaVersion);
  const invalidation = provenance.invalidationState || columnInvalidation;
  const invalidated = invalidation === "authoritative_invalidation";
  const reason = provenance.unavailableReason
    || (!v2 ? "legacy_formula_value" : invalidated ? "authoritative_invalidation" : null);
  const value = v2 && !invalidated && !provenance.unavailableReason
    ? column
    : null;
  const inputs = canonicalAmountInputs(projection, {});
  return {
    value,
    reason,
    status: invalidation,
    freshness,
    currentAuctionAmount: provenance.currentAmount ?? inputs.currentAuctionAmount,
    currentAuctionAmountAsOf: provenance.currentAmountEffectiveAt ?? inputs.currentAuctionAmountAsOf,
    currentAuctionAmountKind: provenance.currentAmountKind ?? inputs.currentAuctionAmountKind,
    affidavitPrincipal: provenance.affidavitPrincipal ?? inputs.affidavitPrincipal,
    affidavitPrincipalAsOf: provenance.principalAsOf ?? inputs.affidavitPrincipalAsOf,
    lastKnownValue: provenance.lastKnownEquity ?? column,
    lastKnownAt: provenance.lastKnownAt ?? provenance.computedAt,
    formulaVersion
  };
}

function canonicalAmountInputs(
  projection: Record<string, unknown>,
  raw: Record<string, unknown>
): Pick<
  LeadBriefAuctionEquity,
  "currentAuctionAmount" | "currentAuctionAmountAsOf" | "currentAuctionAmountKind" | "affidavitPrincipal" | "affidavitPrincipalAsOf"
> {
  const facts = asRecord(asRecord(projection.affidavitFacts).facts);
  const principalFact = mapFact(facts.statedPrincipal, "money");
  const principalAsOf = mapFact(facts.principalAsOfDate ?? facts.asOfDate, "date");
  const currentAuctionAmount = numberKeepingZero(raw.currentAmount ?? projection.currentAmount);
  return {
    currentAuctionAmount,
    currentAuctionAmountAsOf: currentAuctionAmount == null ? null : dateOnly(
      text(raw.currentAmountEffectiveAt)
      || text(projection.upsetFilingDate)
      || text(projection.heldSaleDate)
    ),
    currentAuctionAmountKind: currentAuctionAmount == null
      ? null
      : text(raw.currentAmountKind)
        || (projection.latestUpsetAmount != null ? "upset_bid" : projection.saleAmount != null ? "reported_sale_amount" : null),
    affidavitPrincipal: numberKeepingZero(raw.affidavitPrincipal) ?? factMoney(principalFact),
    affidavitPrincipalAsOf: dateOnly(text(raw.principalAsOf) || principalAsOf.value)
  };
}

function hasCanonicalProjection(record: Record<string, unknown>): boolean {
  return Boolean(record.ecourts_case && typeof record.ecourts_case === "object" && !Array.isArray(record.ecourts_case));
}

function factMoney(fact: LeadBriefFact): number | null {
  if (fact.outcome !== "accepted" || !fact.value) return null;
  return numberKeepingZero(fact.value.replace(/[$,]/g, ""));
}

function numberKeepingZero(value: unknown): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const parsed = Number(String(value).replace(/[$,]/g, "").trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function ecourtsFreshnessLabel(projection: Record<string, unknown>, ecourts: Record<string, unknown>): string | null {
  const freshness = asRecord(projection.freshness);
  const completeness = asRecord(projection.completeness);
  return text(freshness.factFreshness)
    || text(freshness.analysisFreshness)
    || text(completeness.stageOutcome)
    || (text(ecourts.checkedAt) ? "accepted" : null);
}
